import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Group, Loader, Stack, Switch, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import {
    getOrganizationNotificationSettings,
    updateOrganizationNotificationSettings,
} from '../../api/resources';
import type { NotificationSettings } from '../../types/api';
import { DetailSection } from './DetailSection';

type NotificationEvent = keyof NotificationSettings;

/** In the order the visit lifecycle happens, so the list reads as a story. */
const EVENTS: NotificationEvent[] = ['visitCreated', 'visitAssigned', 'reportSubmitted'];

const NOTIFICATION_SETTINGS_QUERY_KEY_PREFIX = 'organizations';

export const notificationSettingsQueryKey = (organizationId: string) => [
    NOTIFICATION_SETTINGS_QUERY_KEY_PREFIX,
    organizationId,
    'notification-settings',
];

/**
 * Per-event email suppression for one organization.
 *
 * The server is the source of truth: it resolves absent settings to enabled and
 * merges partial patches, so each switch sends only the event it changed and the
 * toggles never have to reconcile a full object locally.
 */
export function OrganizationNotificationSettings({
    organizationId,
}: {
    organizationId: string;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const queryKey = notificationSettingsQueryKey(organizationId);

    const settingsQuery = useQuery({
        queryKey,
        queryFn: () => getOrganizationNotificationSettings(organizationId),
    });

    const mutation = useMutation({
        mutationFn: ({ event, enabled }: { event: NotificationEvent; enabled: boolean }) =>
            updateOrganizationNotificationSettings(organizationId, { [event]: enabled }),
        onSuccess: (updated) => {
            queryClient.setQueryData(queryKey, updated);
            // The organization detail drawer also renders raw settings JSON.
            void queryClient.invalidateQueries({ queryKey: ['organizations'] });
            notifications.show({
                color: 'green',
                message: t('features.organizations.notificationSettings.updated'),
            });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    if (settingsQuery.isError) {
        return (
            <DetailSection title={t('features.organizations.notificationSettings.title')}>
                <Alert color="red" variant="light">
                    {t('features.organizations.notificationSettings.loadFailed')}
                </Alert>
            </DetailSection>
        );
    }

    if (settingsQuery.isLoading) {
        return (
            <Group gap="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">
                    {t('features.organizations.notificationSettings.loading')}
                </Text>
            </Group>
        );
    }

    const settings = settingsQuery.data;

    return (
        <DetailSection
            title={t('features.organizations.notificationSettings.title')}
            description={t('features.organizations.notificationSettings.description')}
        >
            <Stack gap="xs">
                {EVENTS.map((event) => (
                    <Switch
                        key={event}
                        checked={settings?.[event] ?? true}
                        disabled={mutation.isPending || settingsQuery.isLoading}
                        onChange={(e) =>
                            mutation.mutate({
                                event,
                                enabled: e.currentTarget.checked,
                            })
                        }
                        label={t(`features.organizations.notificationSettings.events.${event}`)}
                        description={t(
                            `features.organizations.notificationSettings.eventDescriptions.${event}`,
                        )}
                    />
                ))}
            </Stack>
        </DetailSection>
    );
}